import { MouseEvent, ReactNode, useEffect, useRef } from 'react';

export type DesignDialogProps = {
  /** Nothing renders while false. */
  open: boolean;
  onClose: () => void;
  /** id of the element inside that names the dialog (aria-labelledby). */
  labelledBy: string;
  children: ReactNode;
  /** Size classes of the panel, e.g. `w-[1280px] h-[780px]`; sensible default. */
  className?: string;
};

const panelClasses =
  'flex flex-col overflow-hidden rounded-sm border border-design-secondary-black bg-white text-design-black dark:bg-design-black dark:text-white max-w-[calc(100vw-4rem)] max-h-[calc(100vh-4rem)]';

/**
 * Design System modal: a scrim, a focused panel and the three ways out of it
 * (Escape, a click on the scrim, the caller's own control). The content -
 * header, body and buttons - is entirely the caller's.
 */
const DesignDialog = ({
  open,
  onClose,
  labelledBy,
  children,
  className,
}: DesignDialogProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus and the body scroll lock belong to the open/close transition alone,
  // so they must not be torn down and redone whenever `onClose` changes.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', closeOnEscape);

    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  // Only the scrim itself closes: a click that bubbled up from the panel has a
  // different target.
  const onScrimClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      data-testid="dialog-scrim"
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-design-black bg-opacity-70"
      onClick={onScrimClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={`${panelClasses} ${className}`}
      >
        {children}
      </div>
    </div>
  );
};

DesignDialog.defaultProps = {
  className: 'w-[1280px] h-[780px]',
};

export default DesignDialog;
